/**
 * @author Rejo Varghese
 * @description Component to show logs
 */
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Typography, message, Row, Col, Select, Modal, Divider } from 'antd';
import { useDispatch } from 'react-redux';
import { loadingAction } from '../../actions/loading-actions';
import { fetchLogsByTrip, fetchTicketLogsByTrip } from '../../actions/home.action';
import ZoomComponent from '../custom-components/zoom-component/zoom.component';
import { DeviceContext } from '../device-provider/device-provider';
import { LogsTable } from './logs-table/logs-table';
import { LogsModalBody } from './logs-modal/logs-modal.body';
import { getReadableDate, getTimeStampInReadableFormat } from '../../utils/date.utils';
import { isValid, isValidArray, isValidArrayWithValues } from '../../utils/utilities';

const {Text} = Typography;
const { Option } = Select;

const LogsComponent = (props) => {
    const{
        tripId,
        scheduleDescription,
        travelDate,
        startTime,
        scheduleId,
        supportedFare,
        seatGroupIds
    } = props;

    const dispatch = useDispatch();

    const [logsData, setLogsData] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [uniqueAgents, setUniqueAgents] = useState([]);
    const [ticketLogsData, setTicketLogsData] = useState([]);
    const [showTicketLogsModal, setShowTicketLogsModal] = useState(false);

    const {
        isMobile
    }=useContext(DeviceContext);

    useEffect(()=>{
        setLoading(true);
        fetchLogsByTrip(scheduleId,tripId,onSuccess,onFailure);
    },[tripId]);

    const setLoading=(bool)=>{
        dispatch(loadingAction(bool));
    }

    const onSuccess=(data)=>{
        setLogsData(data);
        setLoading(false);
    }

    const onFailure=(msg)=>{
        message.error(msg);
        setLoading(false);
    }

    const onShowTicketLogs = () => {
        setLoading(true);
        fetchTicketLogsByTrip(tripId, (data) => {
            setLoading(false);
            if (isValidArrayWithValues(data)) {
                setTicketLogsData(data);
                setShowTicketLogsModal(true);
            } else {
                message.info("No changes to tickets.");
            }
        }, (msg) => {
            setLoading(false);
            message.error(msg);
        });
    }

    const handleTicketLogsModalClose = () => {
        setShowTicketLogsModal(false);
    }

    // Extract unique agent names from logsData
    const extractUniqueAgents = (data) => {
        const agentSet = new Set();

        if (isValidArrayWithValues(data)) {
            data.forEach(log => {
                // Check top-level agent field
                if (isValid(log.agent)) {
                    agentSet.add(log.agent);
                }

                // Check rawData for nested agent fields
                if (isValid(log.rawData)) {
                    if (isValidArray(log.rawData)) {
                        // If rawData is an array, check each item
                        log.rawData.forEach(item => {
                            if (isValid(item.agent)) agentSet.add(item.agent);
                            if (isValid(item.user_name)) agentSet.add(item.user_name);
                            if (isValid(item.updated_by)) agentSet.add(item.updated_by);
                            if (isValid(item.sent_by)) agentSet.add(item.sent_by);
                        });
                    } else if (typeof log.rawData === 'object') {
                        // If rawData is an object, check directly
                        if (isValid(log.rawData.agent)) agentSet.add(log.rawData.agent);
                        if (isValid(log.rawData.user_name)) agentSet.add(log.rawData.user_name);
                        if (isValid(log.rawData.updated_by)) agentSet.add(log.rawData.updated_by);
                        if (isValid(log.rawData.sent_by)) agentSet.add(log.rawData.sent_by);
                    }
                }
            });
        }
        return Array.from(agentSet).sort();
    };

    // Extract unique agents when logsData changes
    useEffect(() => {
        if (logsData.length > 0) {
            const agents = extractUniqueAgents(logsData);
            setUniqueAgents(agents);
        } else {
            setUniqueAgents([]);
        }
        // Reset selected agent when logsData changes
        setSelectedAgent(null);
    }, [logsData]);

    // Helper function to check if an item matches the selected agent
    const itemMatchesAgent = (item, agent) => {
        if (!agent || !item) return true;
        return (isValid(item.agent) && item.agent === agent) ||
               (isValid(item.user_name) && item.user_name === agent) ||
               (isValid(item.updated_by) && item.updated_by === agent) ||
               (isValid(item.sent_by) && item.sent_by === agent);
    };

    // Helper function to filter rawData
    const filterRawData = (rawData, agent) => {
        if (!agent || !rawData) return rawData;

        if (isValidArray(rawData)) {
            // Filter array to only include items matching the selected agent
            return rawData.filter(item => itemMatchesAgent(item, agent));
        } else if (typeof rawData === 'object' && rawData !== null) {
            // For objects, check for nested arrays that might need filtering
            const filteredObj = { ...rawData };

            // Check common nested array fields that might contain agent info
            Object.keys(filteredObj).forEach(key => {
                if (isValidArray(filteredObj[key])) {
                    // Filter nested arrays
                    filteredObj[key] = filteredObj[key].filter(item => itemMatchesAgent(item, agent));
                }
            });

            return filteredObj;
        }

        return rawData;
    };

    // Filter logsData and rawData based on selected agent
    const filteredLogsData = useMemo(() => {
        if (!selectedAgent) {
            return logsData;
        }

        return logsData
            .filter(log => {
                // Check top-level agent field
                if (isValid(log.agent) && log.agent === selectedAgent) {
                    return true;
                }

                // Check rawData for nested agent fields
                if (isValid(log.rawData)) {
                    if (isValidArray(log.rawData)) {
                        // If rawData is an array, check if any item matches
                        return log.rawData.some(item => itemMatchesAgent(item, selectedAgent));
                    } else if (typeof log.rawData === 'object') {
                        // If rawData is an object, check directly
                        return itemMatchesAgent(log.rawData, selectedAgent);
                    }
                }

                return false;
            })
            .map(log => {
                // Create a new log object with filtered rawData
                const filteredLog = { ...log };

                // Always filter rawData to only include entries matching the selected agent
                // Even if top-level agent matches, we still filter rawData for consistency
                if (isValid(log.rawData)) {
                    filteredLog.rawData = filterRawData(log.rawData, selectedAgent);
                }

                return filteredLog;
            })
            .filter(log => {
                // Remove logs where rawData array was filtered to empty
                // But keep logs where top-level agent matches (even if rawData is empty)
                if (isValid(log.agent) && log.agent === selectedAgent) {
                    return true; // Keep if top-level agent matches
                }
                if (isValidArray(log.rawData) && log.rawData.length === 0) {
                    return false; // Remove if rawData array is empty and no top-level match
                }
                return true;
            });
    }, [logsData, selectedAgent]);

    const handleAgentChange = (value) => {
        setSelectedAgent(value || null);
    };

    return (
        <ZoomComponent isZoomEnabled={isMobile}>
            <div className='tab-block'>
                {logsData.length>0&&
                    <Row gutter={[16,16]} justify="center" align="middle">
                        <Col span={24}>
                            <Row gutter={[16,16]} style={{ marginTop: 16 , marginLeft: 16}} align="middle">
                                <Col>
                                    <Text strong>Filter by Agent: </Text>
                                </Col>
                                <Col>
                                    <Select
                                        size="large"
                                        showSearch
                                        placeholder="Select an agent (or leave empty for all)"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        bordered={true}
                                        className="defalut-select-style"
                                        value={selectedAgent}
                                        onChange={handleAgentChange}
                                        style={{ minWidth: 200 }}
                                        allowClear
                                    >
                                        {uniqueAgents.map(agent => (
                                            <Option key={agent} value={agent}>
                                                {agent}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col>
                                    <a style={{textDecorationLine: 'underline'}} onClick={onShowTicketLogs}>
                                        Show Ticket Logs
                                    </a>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <LogsTable
                                dataSource = {filteredLogsData}
                                scheduleDescription = {scheduleDescription}
                                travelDate={travelDate}
                                startTime = {startTime}
                                supportedFare ={supportedFare}
                                seatGroupIds={seatGroupIds}
                            />
                        </Col>
                    </Row>
                }
                <Modal
                    title={"Ticket Logs"}
                    onOk={handleTicketLogsModalClose}
                    onCancel={handleTicketLogsModalClose}
                    visible={showTicketLogsModal}
                    width={"90vw"}
                    footer={null}
                    destroyOnClose
                >
                    <div>
                        <Text style={{fontWeight: 'bold'}}>{scheduleDescription}</Text>
                        <br/>
                        <Text style={{fontWeight: 'bold'}}>{isValid(travelDate)? getReadableDate(travelDate):''} {isValid(startTime)?getTimeStampInReadableFormat(startTime):''}</Text>
                        <Divider />
                        {isValidArrayWithValues(ticketLogsData) && ticketLogsData.map((logEntry, idx) => (
                            <div key={`${logEntry.action}_${idx}`}>
                                <Row align="middle" justify="center">
                                    <Text style={{fontWeight: 'bold', textDecorationLine: 'underline'}}>{logEntry.action}</Text>
                                </Row>
                                <LogsModalBody
                                    selectedLog={logEntry}
                                    seatGroupIds={seatGroupIds}
                                    supportedFare={supportedFare}
                                />
                                {idx < ticketLogsData.length - 1 && <Divider />}
                            </div>
                        ))}
                    </div>
                </Modal>
            </div>
        </ZoomComponent>
    )
}

export default LogsComponent